import { parse, format } from 'date-fns'
import { convertToLocalTime, convertToTimeZone, formatToTimeZone } from 'date-fns-timezone'
import { getI18n } from '@/libs/i18n'
import { CDN_HOST, DEFAULT_TIMEZONE_NAME, AI_DISABLED_ACCOUNTS, AI_DISABLED_ACCOUNT_NUMS } from '@/libs/constants'
import nacl from 'tweetnacl'
import naclUtil from 'tweetnacl-util'
import { unified } from 'unified'
import remarkGfm from 'remark-gfm'
import markdown from 'remark-parse'
import remark2rehype from 'remark-rehype'
import html from 'rehype-stringify'
import rehypePrismPlus from 'rehype-prism-plus'
import rehypeExternalLinks from 'rehype-external-links'

const URL_PROTOCOL_REGEX = /^https?:\/\//i

export const TLD_LIST = 'ac|ad|ae|af|ag|ai|al|am|an|ao|aq|ar|as|at|au|aw|ax|az|ba|bb|be|bf|bg|bh|bi|bj|bm|bo|br|bs|bt|bv|bw|by|bz|ca|cc|cd|cf|cg|ch|ci|cl|cm|cn|co|cr|cu|cv|cw|cx|cz|de|dj|dk|dm|do|dz|ec|ee|eg|es|et|eu|fi|fm|fo|fr|ga|gb|gd|ge|gf|gg|gh|gi|gl|gm|gn|gp|gq|gr|gs|gt|gw|gy|hk|hm|hn|hr|ht|hu|id|ie|il|im|in|io|iq|ir|is|it|je|jo|jp|kg|ki|km|kn|kp|kr|ky|kz|la|lb|lc|li|lk|lr|ls|lt|lu|lv|ly|ma|mc|md|me|mg|mh|mk|ml|mn|mo|mp|mq|mr|ms|mt|mu|mv|mw|mx|my|na|nc|ne|nf|ng|nl|no|nr|nu|nz|om|pa|pe|pf|ph|pk|pl|pm|pn|pr|ps|pt|pw|py|qa|re|ro|rs|ru|rw|sa|sb|sc|sd|se|sg|sh|si|sj|sk|sl|sm|sn|so|sr|st|su|sv|sx|sy|sz|tc|td|tf|tg|th|tj|tk|tl|tm|tn|to|tp|tr|tt|tv|tw|tz|ua|ug|uk|us|uy|uz|va|vc|ve|vg|vi|vn|vu|wf|ws|yt'.split('|')
export const SLD_LIST = 'co|com|edu|gov|net|mil|org|nom|sch|caa|res|off|gob|int|tur|ip6|uri|urn|asn|act|nsw|qld|tas|vic|pro|biz|adm|adv|agr|arq|art|ato|bio|bmd|cim|cng|cnt|ecn|eco|emp|eng|esp|etc|eti|far|fnd|fot|fst|g12|ggf|imb|ind|inf|jor|jus|leg|lel|mat|med|mus|not|ntr|odo|ppg|psc|psi|qsl|rec|slg|srv|teo|tmp|trd|vet|zlg|web|ltd|sld|pol|fin|k12|lib|pri|aip|fie|eun|sci|prd|cci|pvt|mod|idv|rel|sex|gen|nic|abr|bas|cal|cam|emr|fvg|laz|lig|lom|mar|mol|pmn|pug|sar|sic|taa|tos|umb|vao|vda|ven|mie|北海道|和歌山|神奈川|鹿児島|ass|rep|tra|per|ngo|soc|grp|plc|its|air|and|bus|can|ddr|jfk|mad|nrw|nyc|ski|spy|tcm|ulm|usa|war|fhs|vgs|dep|eid|fet|fla|flå|gol|hof|hol|sel|vik|cri|iwi|ing|abo|fam|gok|gon|gop|gos|aid|atm|gsm|sos|elk|waw|est|aca|bar|cpa|jur|law|sec|plo|www|bir|cbg|jar|khv|msk|nov|nsk|ptz|rnd|spb|stv|tom|tsk|udm|vrn|cmw|kms|nkz|snz|pub|fhv|red|ens|nat|rns|rnu|bbs|tel|bel|kep|nhs|dni|fed|isa|nsn|gub|e12|tec|орг|обр|упр|alt|nis|jpn|mex|ath|iki|nid|gda|inc'.split('|')

export function has2Tld (domain) {
  let parser = document.createElement('a')
  parser.href = domain
  let subdomains = parser.hostname.split('.')
  return subdomains.length >= 2 &&
    TLD_LIST.concat(SLD_LIST).includes(subdomains[subdomains.length - 1]) &&
    SLD_LIST.includes(subdomains[subdomains.length - 2])
}

export function isString (value) {
  return typeof value === 'string'
}

export function isAiDisabledAccount (userData) {
  if (!userData) return false
  const { userId, userNum } = userData
  if (isString(userId) && AI_DISABLED_ACCOUNTS.includes(userId)) return true
  if (isString(userNum) && AI_DISABLED_ACCOUNT_NUMS.includes(userNum)) return true
  return false
}

let homeUrl = 'http://localhost:9500'
if (process.env.VUE_APP_HOME_URL) {
  homeUrl = process.env.VUE_APP_HOME_URL
}

let baUrl = 'https://analytics.beusable.net'
if (process.env.VUE_APP_BA_URL) {
  baUrl = process.env.VUE_APP_BA_URL
}

export const BA_URL = baUrl

let beuablyUrl = 'https://www.beusably.net'
if (process.env.VUE_APP_BEUSABLY_URL) {
  beuablyUrl = process.env.VUE_APP_BEUSABLY_URL
}

let geoUrl = 'https://geo.beusable.net'
if (process.env.VUE_APP_GEO_URL) {
  geoUrl = process.env.VUE_APP_GEO_URL
}

export const BEUSABLY_URL = beuablyUrl
export const GEO_URL = geoUrl

export function isArray (value) {
  return Array.isArray(value)
}

export function isPlainObject (value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

export function isInteger (value) {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value
}

export function upperFirst (value) {
  value = String(value)

  if (!value.length) {
    return value
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function groupBy (arr, key) {
  return arr.reduce((acc, cur) => {
    if (!acc[cur[key]]) {
      acc[cur[key]] = []
    }
    acc[cur[key]].push(cur)
    return acc
  }, {})
}

export function partition (arr, size) {
  return arr.reduce((acc, cur, idx) => {
    if (idx % size === 0) {
      acc.push([cur])
    } else {
      acc[acc.length - 1].push(cur)
    }
    return acc
  }, [])
}

function collectionFn (arr, fieldOrIndex, operation) {
  return arr.reduce((acc, cur, idx) => {
    let value = cur

    if (Array.isArray(cur) && cur[fieldOrIndex] !== undefined) {
      value = cur[fieldOrIndex]
    } else if (typeof cur === 'object' && cur.hasOwnProperty(fieldOrIndex)) {
      value = cur[fieldOrIndex]
    }

    switch (operation) {
      case 'sum':
        if (value === null || !isFinite(value)) {
          value = 0
        }
        return acc + value
      default:
        throw new Error(`invalid operation: ${operation}`)
    }
  }, 0)
}

export function sum (arr, fieldOrIndex) {
  if (!isArray(arr)) {
    return 0
  }
  return collectionFn(arr, fieldOrIndex, 'sum')
}

export function cloneDeep (data) {
  return JSON.parse(JSON.stringify(data))
}

const charToUnicode = (str, lowerCase) => {
  if (!str) return ''
  if (lowerCase) {
    str = str.toLowerCase()
  }

  let unicode = ''
  for (let i = 0, l = str.length; i < l; i++) {
    const uc = str[i].charCodeAt(0).toString(16)
    unicode += '0'.repeat(uc.length > 4 ? 0 : 4 - uc.length) + uc
  }
  return String(unicode)
}

export function compare09aAzZ (a, b) {
  // localeCompare가 mac에서는 0-9 aA-zZ 가-힣, windows에서 0-9 가-힣 aA-zZ가 되어서 아예 유니코드로 순서로 비교함
  return charToUnicode(a.domainName, true).localeCompare(charToUnicode(b.domainName, true))
}

export function compare09azAZ (a, b) {
  const upsidedown = (s) => s.split('').map((c) => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join('')
  return charToUnicode(upsidedown(a)).localeCompare(charToUnicode(upsidedown(b)))
}

export function openBaManagePlan () {
  const URL = `${baUrl}/tool/user/manageplan`
  let win = window.open(URL, '_blank')
  win.focus()
}

export function openGeoManagePlan () {
  const URL = `${geoUrl}/user/manageplan`
  let win = window.open(URL, '_blank')
  win.focus()
}

export function goHome () {
  location.href = homeUrl
}

// https://gist.github.com/beaucharman/e46b8e4d03ef30480d7f4db5a78498ca
export function throttle (callback, delay) {
  let isThrottled = false
  let context, args

  function wrapper () {
    if (isThrottled) {
      args = arguments
      context = this
      return
    }

    isThrottled = true
    callback.apply(this, arguments)

    setTimeout(() => {
      isThrottled = false
      if (args) {
        wrapper.apply(context, args)
        args = context = null
      }
    }, delay)
  }
  return wrapper
}

export function debounce (callback, delay) {
  var timer = null

  return function () {
    if (timer) {
      return
    }

    callback.apply(this, arguments)
    timer = setTimeout(() => {
      timer = null
    }, delay)
  }
}

export function pick (obj, keys) {
  const data = {}

  for (const [key, value] of Object.entries(obj)) {
    if (keys.includes(key)) {
      data[key] = value
    }
  }
  return data
}

// https://stackoverflow.com/questions/24586110/resolve-promises-one-after-another-i-e-in-sequence/42324940#42324940
export function sequence (tasks, fn) {
  return tasks.reduce((promise, task) => promise.then(() => fn(task)), Promise.resolve())
}

export function getUrlWithoutProtocol (url) {
  if (!url) {
    return url
  }
  return url.replace(URL_PROTOCOL_REGEX, '')
}

export function getS3Url (pathKey, timestamp) {
  if (!pathKey) {
    return null
  }

  const [bucketName, filePath] = pathKey.split(':')
  let url = `https://s3.ap-northeast-2.amazonaws.com/${bucketName}/${filePath}`

  if (timestamp) {
    url += `?v=${timestamp}`
  }
  return url
}

export function getCdnUrl (path) {
  path = path.replace(/^\/+/, '')
  return `${CDN_HOST}/dream/${path}`
}

export function getPercentage (value, total, digits) {
  if (isNaN(value) || isNaN(total) || total === 0) {
    return 0
  }

  let result = value / total * 100

  if (digits !== undefined) {
    result = Number(result.toFixed(digits))
  }
  return result
}

export function thousand (value) {
  value = Number(value)
  return value.toLocaleString('en-US')
}

export function leadingZero (number) {
  return number < 10 ? '0' + number : number
}

export function capitalize (string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function dateFormatTimezone (value, timezoneName, formatString = 'YYYY-MM-DD') {
  return formatToTimeZone(value, formatString, { timeZone: timezoneName })
}

export function dateToLocalTimezone (value, timezoneName) {
  return convertToLocalTime(value, { timeZone: timezoneName })
}

export function utcToLocalTimezone (value, timezoneName) {
  return convertToTimeZone(value, { timeZone: timezoneName })
}

export function getOffset (timezoneName = DEFAULT_TIMEZONE_NAME) {
  const date = new Date()
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezoneName }))
  let offset = (tzDate.getTime() - utcDate.getTime())
  let offset2 = offset / 60000
  let o1 = Math.abs(offset2)
  const offsetValue1 = (offset2 >= 0 ? '+' : '-') + ('00' + Math.floor(o1 / 60)).slice(-2) + ':' + ('00' + (o1 % 60)).slice(-2)
  return offsetValue1
}

export function getLocaleDateStr (value, defaultStr) {
  if (!value) {
    return defaultStr || null
  }

  const locale = getI18n().locale
  let formatStr

  switch (locale) {
    case 'en':
      formatStr = 'MMM D, YYYY'
      break
    case 'ko':
      formatStr = 'YYYY년 M월 D일'
      break
    case 'ja':
      formatStr = 'YYYY年MM月DD日'
      break
    default:
      throw new Error(`invalid locale: ${locale}`)
  }
  return format(parse(value), formatStr)
}

export function getRankStr (value) {
  if (typeof value !== 'number') {
    return value
  }

  const locale = getI18n().locale

  switch (locale) {
    case 'ko':
      return value + '위'
    case 'ja':
      return value + '位'
    default:
      if (value === 1) {
        return '1st'
      } else if (value === 2) {
        return '2nd'
      } else if (value === 3) {
        return '3rd'
      } else {
        return value + 'th'
      }
  }
}

export function getPluralString (value, str, prefix = 's') {
  if (value > 1) {
    return str + prefix
  }
  return str
}

export function getTimeParts (ms, secFixedNum = 0) {
  ms = ms || 0
  ms = Math.round(ms)

  let s = Math.floor(ms / 1000)
  let m = Math.floor(s / 60)
  s = s % 60

  if (secFixedNum > 0) {
    s = Number(((ms / 1000) % 60).toFixed(secFixedNum))
  }

  let h = Math.floor(m / 60)
  m = m % 60
  let d = Math.floor(h / 24)
  h = h % 24
  const parts = {
    d,
    h,
    m,
    s
  }
  return parts
}

export function getLocale () {
  const i18n = getI18n()

  if (i18n) {
    throw new Error('i18n not initialized')
  }
  return i18n.locale
}

function getBrowserInfo () {
  const uaContains = (str) => window.navigator.userAgent.indexOf(str) !== -1
  let name = null
  let platform = null

  if (uaContains('Windows')) {
    platform = 'window'

    if (uaContains('MSIE') || uaContains('Trident/')) {
      name = 'ie'
    }
  }

  if (uaContains('Firefox')) {
    name = 'firefox'
  }

  if (uaContains('Chrome')) {
    name = 'chrome'
  }

  if (uaContains('Safari') && !uaContains('Chrome')) {
    name = 'safari'
  }

  if (uaContains('Edge')) {
    name = 'edge'
  }

  return {
    name,
    platform
  }
}

export const browserInfo = getBrowserInfo()

// https://codepen.io/code_monk/pen/FvpfI
export function getRandomHex (len) {
  const maxlen = 8
  const min = Math.pow(16, Math.min(len, maxlen) - 1)
  const max = Math.pow(16, Math.min(len, maxlen)) - 1
  const n = Math.floor(Math.random() * (max - min + 1)) + min
  let r = n.toString(16)
  while (r.length < len) {
    r = r + getRandomHex(len - maxlen)
  }
  return r
}

// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
function b64DecodeUnicode (str) {
  return decodeURIComponent(
    Array.prototype.map.call(atob(str), c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''))
}

export function parseJwt (token) {
  return JSON.parse(
    b64DecodeUnicode(
      token.split('.')[1].replace('-', '+').replace('_', '/')
    )
  )
}

export function loadScript (src) {
  return new Promise((resolve, reject) => {
    const id = `#${src}`

    if (document.getElementById(id)) {
      return resolve()
    }

    const el = document.createElement('script')
    el.src = src
    el.setAttribute('id', id)
    el.onload = resolve
    el.onerror = reject
    document.body.appendChild(el)
  })
}

export function getEncryptedUserNum (userNum) {
  if (!userNum) {
    throw new Error('userNum required')
  }
  return `${userNum}${getRandomHex(8)}`
}

export function triggerClickEvent () {
  let event

  if (typeof Event === 'function') {
    event = new Event('click')
  } else {
    event = document.createEvent('Event')
    event.initEvent('click', false, true)
  }
  document.body.dispatchEvent(event)
}

export function openPricing () {
  const URL = `${homeUrl}/uxheatmap/pricing`
  let win = window.open(URL, '_blank')
  win.focus()
}

export function openContactUs (userData = null, content = null, adminPlan, locale = null) {
  console.log(userData, 'this.userData')
  const sendData = {
    info: {
      email: userData.userId,
      organization: userData.userName,
      category: 'contactUs',
      lang: getI18n().locale
    },
    content: content
  }

  const solomonIframe = document.createElement('iframe')
  const solomonWrap = document.createElement('div')

  solomonWrap.appendChild(solomonIframe)
  solomonWrap.classList.add('solomon-wrap')
  solomonIframe.src = process.env.VUE_APP_SOLOMON_URL || 'https://solomon.beusable.net/beusChat'
  solomonIframe.classList.add('solomon-iframe')

  solomonIframe.onload = () => {
    solomonIframe.contentWindow.postMessage({ event: 'sendQuestion', msg: sendData }, '*')
  }

  document.body.appendChild(solomonWrap)

  window.addEventListener('message', (e) => {
    if (e.data.event === 'close') {
      document.body.removeChild(solomonWrap)
    }
  })
}

export function encrypt (data, key) {
  function padEnd (string, targetLength, padString) {
    targetLength = targetLength >> 0
    padString = String((typeof padString !== 'undefined' ? padString : ' '))
    if (string.length > targetLength) {
      return string.slice(0, 32)
    } else {
      targetLength = targetLength - string.length
      if (targetLength > padString.length) {
        padString += padString.repeat(targetLength / padString.length)
      }
      return string + padString.slice(0, targetLength)
    }
  }

  function textEncode (str) {
    if (window.TextEncoder) {
      return new TextEncoder('utf-8').encode(str)
    }
    let utf8 = unescape(encodeURIComponent(str))
    let result = new Uint8Array(utf8.length)
    for (let i = 0; i < utf8.length; i++) {
      result[i] = utf8.charCodeAt(i)
    }
    return result
  }

  const encodeBase64 = naclUtil.encodeBase64
  const nonce = nacl.randomBytes(24)
  const secretKey = textEncode(padEnd(key, 32, '0'))
  const secretData = textEncode(data)
  const encrypted = nacl.secretbox(secretData, nonce, secretKey)
  return `${encodeBase64(nonce)}:${encodeBase64(encrypted)}`
}

export function exchangePrice (price) {
  const locale = getI18n().locale

  switch (locale) {
    case 'en':
      return price / 500
    case 'ko':
      return price
    case 'ja':
      return price / (20 / 3)
    default:
      return price
  }
}

export function isMatchedURL (url, captureUrl, matchType, matchString) {
  let parsedURL
  let parsedCaptureURL

  try {
    parsedURL = new URL(url)
    parsedCaptureURL = new URL(captureUrl)
  } catch (e) {
    // logger.error(`${LOG_PREFIX} ${apply._id}${e}`);
    return false
  }

  console.log(parsedURL, parsedCaptureURL, matchType, matchString)

  let parsedHost = parsedURL.host

  let isMatchedURL = false
  if (matchType === 'simple') {
    const pathname = parsedURL.pathname || ''
    isMatchedURL = parsedCaptureURL.pathname === pathname
  } else if (matchType === 'exact') {
    isMatchedURL = captureUrl === url
  } else if (matchType === 'start') {
    isMatchedURL = parsedHost === parsedCaptureURL.host && captureUrl.indexOf(url) === 0
  } else if (matchType === 'end' && matchString) {
    const revURL = captureUrl.split('').reverse().join('')
    const revMatchString = matchString.split('').reverse().join('')
    isMatchedURL = parsedHost === parsedCaptureURL.host && revURL.indexOf(revMatchString) === 0
  } else if (matchType === 'contains' && matchString) {
    isMatchedURL = parsedHost === parsedCaptureURL.host && captureUrl.indexOf(matchString) > -1
  } else if (matchType === 'regexp' && matchString) {
    const regexp = new RegExp(matchString)
    isMatchedURL = parsedHost === parsedCaptureURL.host && regexp.test(captureUrl)
  }
  // logger.debug(`${LOG_PREFIX} [IsMatchedURL] aid=${apply._id}, matchType=${matchType}, matchString=${matchString}, applyURL=${applyURL}, applyPathname=${applyPathname}, url=${url}, => isMatchedURL: ${isMatchedURL}`);
  return isMatchedURL
}

export function getDefaultDevice (info) {
  const deviceList = []
  for (const key of Object.keys(info['device'])) {
    const device = key
    const pv = info['device'][key]['pv']
    if (device && pv) {
      deviceList.push({
        device: device,
        pv: pv
      })
    }
  }
  const items = deviceList.sort((a, b) => {
    return b.pv - a.pv
  })
  return items[0]['device']
}

export function getDefaultDeviceAB (infoA, infoB) {
  const deviceList = ['desktop', 'tablet', 'phone']
  const defaultDeviceList = []
  const noDevice = [
    {
      device: 'desktop',
      pv: 0
    },
    {
      device: 'tablet',
      pv: 0
    },
    {
      device: 'phone',
      pv: 0
    }
  ]
  for (const key of deviceList) {
    let pv = 0
    if (infoA['device'][key] && infoB['device'][key]) {
      pv = infoA['device'][key]['pv'] + infoB['device'][key]['pv']
    } else if (infoA['device'][key] && !infoB['device'][key]) {
      pv = infoA['device'][key]['pv']
    } else if (!infoA['device'][key] && infoB['device'][key]) {
      pv = infoB['device'][key]['pv']
    }
    if (key && pv) {
      defaultDeviceList.push({
        device: key,
        pv: pv
      })
    }
  }
  if (defaultDeviceList.length > 0) {
    const items = defaultDeviceList.sort((a, b) => {
      return b.pv - a.pv
    })
    return items
  } else {
    return noDevice
  }
}

export const convertMarkdownToHtml = (markDownText) => {
  const htmlText = unified()
    .use(markdown)
    .use(remarkGfm, { singleTilde: false })
    .use(remark2rehype)
    .use(rehypePrismPlus)
    .use(rehypeExternalLinks, { rel: ['nofollow'], target: '_blank' })
    .use(html)
    .processSync(markDownText)
  return htmlText
}
